import React from 'react'

import * as S from './styles'
import { useCaseConclusion } from './useCaseConclusion'
import { Image, Testimonial, Text } from '@/components'
import { useIsVisibleElement } from '@/hooks/useIsVisibleElement'
import { Conclusion } from '@/service/api/casePage'

export type CasesConclusionContainerProps = {
  /**
   * Sets the colour of the title's underline
   * @example
   * ```tsx
   * <CaseConclusionContainer color='black' />
   * ```
   * */
  color?: string

  /**
   * Data fetched from API
   * @example
   * ```tsx
   * <CaseConclusionContainer data={data} />
   * ```
   * */
  data: Conclusion

  /**
   * function redirect to next case
   *
   * @example
   * ```tsx
   * <CaseConclusionContainer onPush={onPush} />
   * ```
   *
   */
  onPush: () => void
  isNextCase: boolean
}

export const CaseConclusionContainer: React.FC<
  CasesConclusionContainerProps
> = ({ color, data, onPush, isNextCase }) => {
  const { fontSizes } = useCaseConclusion()
  const { element, elementVisible } = useIsVisibleElement()
  return (
    <S.CaseConclusionContainer
      underlineColor={color}
      elementVisible={elementVisible}
    >
      <S.CurrentCaseContainer>
        <div>
          <Text size={fontSizes.title} className="title">
            {data.title}
            <span className="bar" />
          </Text>
          <S.TextContainer ref={element}>
            <Text size={fontSizes.description} className="description">
              {data.paragraphLeft}
            </Text>
            {data.paragraphRight && (
              <Text size={fontSizes.description} className="description">
                {data.paragraphRight}
              </Text>
            )}
          </S.TextContainer>
        </div>

        {!data.hideDeposition && (
          <div>
            <Testimonial
              authorPicture={data.testimonialPicture}
              authorName={data.testimonialName}
              authorRole={data.testimonialRole}
              description={data.testimonial}
            />
          </div>
        )}
      </S.CurrentCaseContainer>

      <S.NextCaseContainer onClick={onPush}>
        <Image
          src={data.nextCaseBackgroundImageUrl}
          alt="next case image"
          fill
          style={{ objectFit: 'cover' }}
          priority
        />
        <S.FilterContainer>
          <S.NextCaseData>
            {isNextCase && (
              <Text size="b3" className="next-case">
                {data.nextCaseHeading}
              </Text>
            )}
            <Image
              alt="logo"
              src={data.nextCaseLogo}
              defaultSize={{
                height: 373,
                width: 1440,
              }}
            />
            <Text size="b1">{data.nextCaseDescription}</Text>
          </S.NextCaseData>
        </S.FilterContainer>
      </S.NextCaseContainer>
    </S.CaseConclusionContainer>
  )
}
